import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { formatApiError, tFdType } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus } from "lucide-react";

/**
 * FailureDomainAssignDialog — associe un actif à un domaine de défaillance existant.
 * L'association est many-to-many ; la suppression d'association ne supprime pas le domaine.
 */
export default function FailureDomainAssignDialog({ open, onOpenChange, assetId, current = [], onChanged }) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState("");

  const { data: all = [] } = useQuery({
    queryKey: ["failure-domains"],
    queryFn: async () => (await api.get("/failure-domains")).data,
    enabled: open,
  });

  const currentIds = new Set(current.map((d) => d.id));
  const options = all.filter((d) => !currentIds.has(d.id));

  const associate = useMutation({
    mutationFn: async () => (await api.post("/failure-domains/associate", { asset_id: assetId, domain_id: selected })).data,
    onSuccess: () => { toast.success("Actif associé au domaine"); setSelected(""); qc.invalidateQueries({ queryKey: ["failure-domains"] }); onChanged?.(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border rounded-sm" data-testid="fd-assign-dialog">
        <DialogHeader>
          <DialogTitle className="font-head">Associer à un domaine de défaillance</DialogTitle>
          <DialogDescription>Un actif peut appartenir à plusieurs domaines simultanément.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <Label className="text-xs">Domaine de défaillance</Label>
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger className="rounded-sm" data-testid="fd-assign-select"><SelectValue placeholder="Sélectionner un domaine…" /></SelectTrigger>
            <SelectContent className="max-h-64">
              {options.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">Aucun domaine disponible. Créez-en un dans « Domaines de défaillance ».</div>}
              {options.map((d) => <SelectItem key={d.id} value={d.id}>{d.name} — {tFdType(d.type)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Fermer</Button>
          <Button className="rounded-sm gap-2" onClick={() => associate.mutate()} disabled={!selected || associate.isPending} data-testid="fd-assign-confirm"><Plus className="h-4 w-4" /> Associer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
